import { Button, ControlledCheckbox, FlexContainer } from '@act/storybook';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { membersRequests } from 'redux/requests/members.request';
import { useApiCall } from 'redux/utils/api-hook';
import styled from 'styled-components';
import { SearchBar } from '../components/search-bar';
import FilterButtonArray from './filter-button-array';
import { BaseFilterConfig } from './types';

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 20px;
`;

const ButtonPanel = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
`;

interface MemberFiltersProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  viewMode: string;
  activeFilterCount: number;
  handleOpenFilters: () => void;
  handleFiltering: () => void;
  toggleMyMembers: () => void;
  setViewMode: (string) => void;
  currentFilterConfig: BaseFilterConfig;
}

const MemberFilters = function <T>({
  control,
  viewMode,
  activeFilterCount,
  handleOpenFilters,
  handleFiltering,
  toggleMyMembers,
  setViewMode,
  currentFilterConfig,
}: MemberFiltersProps<T>) {
  const [typeaheadUsers, isLoadingTypeahead, getTypeaheadUsers] = useApiCall(
    membersRequests.getPaginatedMembersForTable
  );
  const [typeaheadOptions, setTypeaheadOptions] = useState<string[]>([]);

  function handleViewModeChange(newViewMode) {
    setViewMode(newViewMode);
  }

  useEffect(() => {
    if (typeaheadUsers?.users) {
      setTypeaheadOptions(
        typeaheadUsers.users.map(u => `${u.firstName} ${u.lastName}`)
      );
    } else {
      setTypeaheadOptions([]);
    }
  }, [typeaheadUsers]);

  function setTypeaheadSearchTerm(searchTerm: string) {
    const overrideFilters = {
      searchTerm,
      'page-start': 0,
      'page-size': 10,
      includeCount: true,
      orderBy: null,
      orderDirection: null,
      includeDetails: true,
    };
    const filterConfig = {
      ...currentFilterConfig,
      ...overrideFilters,
    };
    void getTypeaheadUsers({ params: filterConfig });
  }

  return (
    <FlexContainer
      direction="column"
      height="fit-content"
      width="100%"
      flexShrink={0}
      padding={2}
    >
      <Controller
        control={control}
        name={'searchTerm' as Path<T>}
        render={({ field: { onChange, value } }) => {
          return (
            <SearchBar
              value={value as string}
              label="Search by member name"
              options={typeaheadOptions}
              isLoading={isLoadingTypeahead}
              setTypeaheadSearchTerm={setTypeaheadSearchTerm}
              setSearchTerm={searchTerm => {
                onChange(searchTerm);
                handleFiltering();
              }}
            />
          );
        }}
      ></Controller>
      <OptionsContainer>
        <ControlledCheckbox
          ariaLabel="My members"
          control={control}
          defaultChecked={false}
          label="My members"
          name={'showOnlyMyMembers' as Path<T>}
          onCheck={toggleMyMembers}
        />
        <ButtonPanel>
          <Button
            startIcon={<FilterListIcon />}
            variant="outlined"
            onClick={handleOpenFilters}
          >
            <Typography variant="labelLarge">
              Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
            </Typography>
          </Button>
          <FilterButtonArray
            viewMode={viewMode}
            handleViewModeChange={handleViewModeChange}
          />
        </ButtonPanel>
      </OptionsContainer>
    </FlexContainer>
  );
};

export default MemberFilters;
